if (!customElements.get('vk-collection-campaign')) {
  customElements.define(
    'vk-collection-campaign',
    class CollectionCampaign extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-campaign-track]');
        this.slides = Array.from(this.querySelectorAll('[data-campaign-slide]'));
        this.autoplayControl = this.querySelector('[data-campaign-autoplay]');
        this.status = this.querySelector('[data-campaign-status]');
        this.currentIndex = 0;
        this.scrollFrame = null;
        this.autoplayTimer = null;
        this.resumeTimer = null;
        this.deferredLoadTimer = null;
        this.deferredLoadIdleCallback = null;
        this.announceNextChange = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.userPaused = this.dataset.autoplay !== 'true' || this.reducedMotion.matches;

        if (!this.track || this.slides.length < 2) return;

        this.onScroll = this.onScroll.bind(this);
        this.onPointerDown = this.pauseForInteraction.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onAutoplayClick = this.toggleAutoplay.bind(this);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.addEventListener('keydown', this.onKeyDown);
        this.autoplayControl?.addEventListener('click', this.onAutoplayClick);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.updateState(0);
        this.updateAutoplayControl();
        this.scheduleDeferredSlideLoad();
        this.startAutoplay();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('keydown', this.onKeyDown);
        this.autoplayControl?.removeEventListener('click', this.onAutoplayClick);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopAutoplay();
        window.clearTimeout(this.resumeTimer);
        window.clearTimeout(this.deferredLoadTimer);
        if (this.deferredLoadIdleCallback && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.deferredLoadIdleCallback);
        }
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      }

      onScroll() {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const width = this.track.clientWidth;
          if (!width) return;
          const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
          this.updateState(index);
        });
      }

      onKeyDown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.pauseForInteraction();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        this.goTo((this.currentIndex + delta + this.slides.length) % this.slides.length, true, true);
      }

      onVisibilityChange() {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      }

      pauseForInteraction(event) {
        if (event?.target?.closest?.('[data-campaign-autoplay]')) return;
        if (event?.type === 'pointerdown') {
          this.announceNextChange = true;
          this.loadSlide((this.currentIndex + 1) % this.slides.length);
        }
        this.stopAutoplay();
        window.clearTimeout(this.resumeTimer);
        if (this.userPaused) return;
        this.resumeTimer = window.setTimeout(() => this.startAutoplay(), 7000);
      }

      toggleAutoplay() {
        this.userPaused = !this.userPaused;
        window.clearTimeout(this.resumeTimer);
        if (this.userPaused) this.stopAutoplay();
        else {
          this.scheduleDeferredSlideLoad();
          this.startAutoplay();
        }
        this.updateAutoplayControl();
      }

      loadSlide(index) {
        const slide = this.slides[index];
        if (!slide) return;

        slide.querySelectorAll('[data-campaign-srcset]').forEach((source) => {
          source.setAttribute('srcset', source.dataset.campaignSrcset);
          delete source.dataset.campaignSrcset;
        });
      }

      scheduleDeferredSlideLoad() {
        if (this.userPaused || window.innerWidth > 749 || this.slides.length < 2) return;

        const primaryImage = this.querySelector('[data-campaign-primary-image]');
        const queueLoad = () => {
          const loadNextSlide = () => this.loadSlide(1);
          if ('requestIdleCallback' in window) {
            this.deferredLoadIdleCallback = window.requestIdleCallback(loadNextSlide, { timeout: 1500 });
          } else {
            this.deferredLoadTimer = window.setTimeout(loadNextSlide, 500);
          }
        };

        if (!primaryImage || primaryImage.complete) queueLoad();
        else primaryImage.addEventListener('load', queueLoad, { once: true });
      }

      updateAutoplayControl() {
        if (!this.autoplayControl) return;
        this.autoplayControl.dataset.paused = this.userPaused ? 'true' : 'false';
        this.autoplayControl.setAttribute(
          'aria-label',
          this.userPaused ? 'Play automatic slideshow' : 'Pause automatic slideshow'
        );
      }

      goTo(index, smooth = false, announce = false) {
        if (!this.track) return;
        this.loadSlide(index);
        this.announceNextChange = announce;
        const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
        this.track.scrollTo({ left: this.track.clientWidth * index, behavior });
        this.updateState(index);
      }

      updateState(index) {
        const changed = index !== this.currentIndex;
        this.loadSlide(index);
        this.currentIndex = index;
        this.slides.forEach((slide, slideIndex) => {
          slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
        });
        if (changed && this.announceNextChange && this.status) {
          this.status.textContent = `Image ${index + 1} of ${this.slides.length}`;
          this.announceNextChange = false;
        }
      }

      startAutoplay() {
        this.stopAutoplay();
        if (this.userPaused || document.hidden || window.innerWidth > 749) return;
        const interval = Math.max(3000, Number(this.dataset.interval) || 5000);
        this.autoplayTimer = window.setInterval(() => {
          this.goTo((this.currentIndex + 1) % this.slides.length, true);
        }, interval);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  );
}
